import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import SimpleLayout from '../layout/SimpleLayout';
import { BiCheck, BiCycling, BiRupee, BiWallet } from 'react-icons/bi';
import { SiRazorpay } from "react-icons/si";
import { MdSwapHoriz, MdSecurity } from "react-icons/md";
import { FiChevronDown, FiChevronUp, FiEdit2, FiMapPin, FiPackage, FiCreditCard, FiTrash2 } from "react-icons/fi";
import { toast } from 'react-toastify';
import compressImage from '../utils/compressImage';
import { UserRole } from '../config/userRole';

const messages = [
  { icon: <BiCycling />, text: "Yayy! You got free delivery" },
  { icon: <MdSwapHoriz />, text: "15 days easy return & exchange" },
  { icon: <MdSecurity />, text: "100% Secure Payments" }
];

const CheckoutSkeleton = () => (
  <div className="checkout-page bg-gray-50 min-h-screen">
    <div className="max-w-screen-2xl mx-auto px-4 md:px-10 py-6 animate-pulse">
      <div className="h-7 w-64 bg-gray-200 rounded-lg mb-6" />
      <div className="md:flex-row gap-6 flex-col flex">
        <div className="w-full md:w-1/5 rounded-2xl border border-gray-100 bg-white p-3 space-y-3">
          <div className="h-14 bg-gray-200 rounded-xl" />
          <div className="h-14 bg-gray-200 rounded-xl" />
        </div>
        <div className="w-full md:w-2/5 rounded-2xl border border-gray-100 bg-white p-5 space-y-3">
          <div className="h-4 w-3/4 bg-gray-200 rounded" />
          <div className="h-4 w-1/2 bg-gray-200 rounded" />
          <div className="h-11 w-full bg-gray-200 rounded-xl mt-4" />
        </div>
        <div className="w-full md:w-2/5 space-y-4">
          <div className="h-16 rounded-2xl border border-gray-100 bg-white" />
          <div className="h-16 rounded-2xl border border-gray-100 bg-white" />
          <div className="h-48 rounded-2xl border border-gray-100 bg-white" />
        </div>
      </div>
    </div>
  </div>
);

const Checkout = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('userToken');
  const [pageLoading, setPageLoading] = useState(true);
  const [, setIsLoading] = useState(false);
  const [shippingValidate, setShippingValidate] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [shippingDetails, setShippingDetails] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [activeTab, setActiveTab] = useState("razorpay");
  // Fails open: a product missing the is_cod flag (or not yet loaded) doesn't block COD.
  const codAllowed = cartItems.length === 0 || cartItems.every((item) => Number(item.product?.is_cod ?? 1) === 1);
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isItemsOpen, setIsItemsOpen] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [varify, setVarify] = useState(false);
  const location = useLocation();
  const discount = location.state?.discount;
  const couponCode = location.state?.couponCode;


  useEffect(() => {
    const uservarified = localStorage.getItem('uservarified');
    if (uservarified && uservarified === 'null') {
      setVarify(true);
      return;
    }

  }, [])

  useEffect(() => {
    if (!codAllowed && activeTab === 'cod') setActiveTab('razorpay');
  }, [codAllowed, activeTab]);

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let cartItems = response.data.cart_items || [];

      if (UserRole.userRole === "distributor") {
        cartItems = cartItems.map((item) => {
          const variation = item?.product_variation || (
            Array.isArray(item?.product_variations) && item?.prod_variation_id
              ? item.product_variations.find((v) => v.id === item.prod_variation_id)
              : null
          );
          const packQty = Number(variation?.pack_qty || item?.pack_qty || 0);
          const packPrice = Number(variation?.pack_price || item?.pack_price || 0);
          const unitPrice = packQty > 0 && packPrice > 0
            ? packPrice / packQty
            : Number(item.price ?? item.sale_price ?? 0);
          return { ...item, price: unitPrice, regular_price: item.regular_price ?? item.price ?? item.sale_price };
        });
      }

      if (response.data.status && cartItems.length > 0) {
        setCartItems(cartItems);
      } else {
        navigate('/');
      }

    } catch (error) {
      console.error('Error fetching cart:', error.message);
      navigate('/');
    } finally {
      setPageLoading(false);
    }
  }, [token]);

  const fetchShippingAddresses = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_shipping_addresses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const addresses = response.data.shipping_addresses || [];
      setShippingAddresses(addresses);

      // ✅ Automatically select the first address if available
      if (addresses.length > 0) {
        setSelectedAddress(addresses[0]);
        setShippingDetails(addresses[0]); // Ensure shipping details are also updated
      }
    } catch (error) {
      console.error("Error fetching shipping addresses:", error.message);
    }
  }, [token]);


  useEffect(() => {
    if (!token) {
      navigate('/login');
    } else {
      fetchCart();
      fetchShippingAddresses();
    }
  }, [token, navigate, fetchCart, fetchShippingAddresses]);


  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    setShippingDetails(address);
  };

  // Unit price for a cart line — same distributor pack-price-vs-regular-price
  // logic calculateSummary() uses, so the displayed price always matches what's charged.
  const getItemUnitPrice = (item) => {
    if (UserRole.userRole === "distributor") {
      const variation = item?.product_variation || (
        Array.isArray(item?.product_variations) && item?.prod_variation_id
          ? item.product_variations.find((v) => v.id === item.prod_variation_id)
          : null
      );
      const packQty = Number(variation?.pack_qty || item?.pack_qty || 0);
      const packPrice = Number(variation?.pack_price || item?.pack_price || 0);
      if (packQty > 0 && packPrice > 0) {
        return packPrice / packQty;
      }
    }
    return Number(item.price ?? item.sale_price ?? 0);
  };

  const [removingItemId, setRemovingItemId] = useState(null);

  const removeCartItem = async (item) => {
    if (cartItems.length <= 1) return; // never leave checkout with an empty cart

    const result = await Swal.fire({
      title: 'Remove item?',
      text: 'This item will be removed from your order.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'rgb(var(--brand-600))',
    });
    if (!result.isConfirmed) return;

    setRemovingItemId(item.id);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/delete_cart_item/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status) {
        setCartItems((prev) => prev.filter((i) => i.id !== item.id));
      }
    } catch (error) {
      console.error('Error removing cart item:', error);
      Swal.fire({
        title: 'Could not remove item',
        text: 'Something went wrong. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: 'rgb(var(--brand-600))',
      });
    } finally {
      setRemovingItemId(null);
    }
  };

  const calculateSummary = () => {
    const subtotal = cartItems.reduce((acc, item) => {
      if (UserRole.userRole === "distributor") {
        const variation = item?.product_variation || (
          Array.isArray(item?.product_variations) && item?.prod_variation_id
            ? item.product_variations.find((v) => v.id === item.prod_variation_id)
            : null
        );
        const packQty = Number(variation?.pack_qty || item?.pack_qty || 0);
        const packPrice = Number(variation?.pack_price || item?.pack_price || 0);
        if (packQty > 0 && packPrice > 0) {
          return acc + (Number(item.quantity) / packQty) * packPrice;
        }
      }
      const unitPrice = Number(item.price ?? item.sale_price ?? 0);
      return acc + unitPrice * item.quantity;
    }, 0);
    const totalMRP = UserRole.userRole === "distributor"
      ? subtotal
      : cartItems.reduce((acc, item) => {
          const unitRegular = Number(item.regular_price ?? item.price ?? item.sale_price ?? 0);
          return acc + unitRegular * item.quantity;
        }, 0);
    // Always derive the charged amount from THIS page's own freshly-fetched cart data
    // (subtotal, computed above) rather than trusting a `finalAmount` recomputed on the
    // Cart page and passed through router state — that value can go stale/out of sync
    // with this page's own live cart fetch. Only the coupon discount amount (a value
    // that genuinely originates from the Cart page's coupon validation) is carried over.
    const newSubtotal = subtotal - (discount ? discount : 0);
    return { totalMRP, newSubtotal, savings: totalMRP - newSubtotal };
  };

  const { totalMRP, savings, newSubtotal } = calculateSummary();
  useEffect(() => {
    const payment = async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_payment_mothods`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.status) {
        setPaymentMethods(response.data.payment_methods);
      }

    }
    payment();
  }, [token]);

  const updatedSubtotal = (newSubtotal + (discount ? discount : 0)).toFixed(2); // Add ₹20 if COD
  const roundedSubtotal = Math.round(newSubtotal);
  const roundOff = (roundedSubtotal - newSubtotal).toFixed(2);

  const handleSubmit = async () => {
    const shippingId = shippingDetails.id;
    if (!shippingId) {
      setShippingValidate(true);
      setIsAddressOpen(true);
      return;
    }
    if (paymentMethods.length === 0) {
      Swal.fire({ title: "Error!", text: "Payment methods not loaded. Please refresh and try again.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    setIsLoading(true);
    setLoading(true);
    const isCOD = activeTab !== "razorpay";
    const codMethod  = paymentMethods.find((m) => m.method_name?.toLowerCase().includes("cod") || m.method_name?.toLowerCase().includes("cash")) || paymentMethods[0];
    const onlineMethod = paymentMethods.find((m) => m.method_name?.toLowerCase().includes("razor") || m.method_name?.toLowerCase().includes("online")) || paymentMethods[paymentMethods.length - 1];

    const orderPayload = {
      order_items: cartItems.map((item) => ({
        product_id: item.product_id,
        prod_variation_id: item.prod_variation_id,
        product_name: item.prod_name,
        quantity: item.quantity,
        price: item.price ?? item.sale_price,
        regular_price: item.regular_price ?? item.price ?? item.sale_price,
        total_price: (item.price ?? item.sale_price) * item.quantity,
      })),
      pay_method_id: isCOD ? codMethod.id : onlineMethod.id,
      shipping_address: shippingId,
      bill_amt: Number(updatedSubtotal),
      discount_amt: discount ? Number(discount) : 0, // newSubtotal + discount = updatedSubtotal
      coupon_code: couponCode,
      net_amt: newSubtotal,
      shipping_total: isCOD ? 20 : 0,
      r_off: roundOff,
      pay_amt: roundOff < 0.5 ? (isCOD ? (newSubtotal - roundOff + 20) : (newSubtotal - roundOff)) :
        (isCOD ? (newSubtotal - roundOff + 1 + 20) : (newSubtotal - roundOff + 1)),
    };

    try {
      const orderResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/create_order`, orderPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!orderResponse.data || !orderResponse.data.order_id) {
        Swal.fire({
          title: "Order Creation Failed!",
          text: orderResponse.data.error_message.shipping_address[0] ? orderResponse.data.error_message.shipping_address[0] : "Your order could not be placed. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      if (!isCOD) {
        await initiateRazorpayPayment(orderResponse.data.order_id, newSubtotal);
      } else {
        const paymentResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/add_order_payment`,
          {
            order_id: orderResponse.data.order_id,
            payment_method: "COD",
            payment_status: "pending",
            payment_amount: roundOff < 0.5 ? (isCOD ? (newSubtotal - roundOff + 20) : (newSubtotal - roundOff)) :
              (isCOD ? (newSubtotal - roundOff + 1 + 20) : (newSubtotal - roundOff + 1)),
            payment_reference: "COD_" + orderResponse.data.order_id,
            payment_details: null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (paymentResponse.data.status) {
          Swal.fire({
            title: "Order Placed Successfully!",
            text: "Your order has been placed successfully and will be processed soon.",
            icon: "success",
            confirmButtonText: "OK",
          })
          navigate("/thank-you");
          window.location.replace("/thank-you?nocache=" + new Date().getTime());
        }
      }
    } catch (error) {
      console.error("Error processing order:", error.response?.data || error.message);
      Swal.fire({
        title: "Error!",
        text: "Failed to place order. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Loads Razorpay's SDK only when a payment is actually being initiated, instead
  // of on every page load (it was previously a static <script> in index.html,
  // running background activity on every page — including ones with no payment
  // UI at all — which was interfering with scroll position).
  const loadRazorpayScript = () => {
    if (window.Razorpay) return Promise.resolve(true);
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiateRazorpayPayment = async (orderId, amount) => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      Swal.fire({
        title: "Error!",
        text: "Could not load the payment gateway. Please check your connection and try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const options = {
      key: "rzp_test_RxOgC9sPpereLC",
      amount: amount * 100,
      currency: "INR",
      name: "MPI Virgin Plastic",
      description: "Order Payment",

      handler: async function (response) {
        if (!response.razorpay_payment_id) {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/cancel_order`,
            { order_id: orderId },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {});
          Swal.fire({
            title: "Payment Failed!",
            text: "Payment could not be processed. Your order has been cancelled.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }

        try {
          const paymentResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/add_order_payment`,
            {
              order_id: orderId,
              payment_method: "razorpay",
              payment_status: "completed",
              payment_amount: amount,
              payment_reference: response.razorpay_payment_id,
              payment_details: JSON.stringify(response),
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (paymentResponse.data.status) {
            Swal.fire({
              title: "Success!",
              text: "Order and payment successful!",
              icon: "success",
              confirmButtonText: "OK",
            }).then(() => {
              navigate("/thank-you");
              window.location.replace("/thank-you?nocache=" + new Date().getTime());
            });
          } else {
            Swal.fire({
              title: "Payment Failed!",
              text: paymentResponse.data.message || "Something went wrong.",
              icon: "error",
              confirmButtonText: "Try Again",
            });
          }
        } catch (error) {
          Swal.fire({
            title: "Payment Error!",
            text: "An error occurred while processing the payment.",
            icon: "error",
            confirmButtonText: "Try Again",
          });
        }
      },
      prefill: {
        name: selectedAddress?.full_name || "",
        email: selectedAddress?.email || "",
        contact: selectedAddress?.mobile_number || "",
      },
      theme: {
        color: "#3399cc",
      },
      modal: {
        escape: true,
        ondismiss: async function () {
          try {
            await axios.post(
              `${process.env.REACT_APP_API_URL}/api/v1/add_order_payment`,
              {
                order_id: orderId,
                payment_method: "razorpay",
                payment_status: "cancelled",
                payment_amount: amount,
                payment_reference: null,
                payment_details: JSON.stringify({ message: "Payment was cancelled or dismissed by user" }),
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (err) {
            console.error("Failed to log cancelled payment:", err);
          }

          // Cancel the order so it shows as Cancelled in admin & customer dashboard
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/cancel_order`,
            { order_id: orderId },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {});

          Swal.fire({
            title: "Payment Cancelled!",
            text: "You cancelled the payment. Your order has been cancelled.",
            icon: "warning",
            confirmButtonText: "OK",
          });
        },
      }

    };

    const rzp = new window.Razorpay(options);

    // Razorpay only calls `handler` on a successful payment — genuine gateway
    // failures (card declined, bank timeout, etc.) fire this event instead.
    // Without this listener those failures left no record anywhere.
    rzp.on("payment.failed", async function (response) {
      const err = response?.error || {};
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/add_order_payment`,
          {
            order_id: orderId,
            payment_method: "razorpay",
            payment_status: "failed",
            payment_amount: amount,
            payment_reference: err.metadata?.payment_id || null,
            payment_details: JSON.stringify(err),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.error("Failed to log failed payment:", e);
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/cancel_order`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});

      Swal.fire({
        title: "Payment Failed!",
        text: err.description || "Your payment could not be processed. Your order has been cancelled.",
        icon: "error",
        confirmButtonText: "Try Again",
      });
    });

    rzp.open();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % messages.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, []);

  const resendHandler = async () => {
    setLoading(true);
    setSuccess(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/resend_email_verification`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('userToken')}`
        },
      });

      if (response.data.status) {
        setSuccess("Email sent successfully");
      } else {
        toast.error(response.data.message, { position: "top-center" });
      }
    }
    catch (error) {
      console.error("Error:", error.response ? error.response.data : error);
    } finally {
      setLoading(false);
    }
  }

  if (pageLoading) {
    return (
      <SimpleLayout title='Checkout' link='javascript:;'>
        <CheckoutSkeleton />
      </SimpleLayout>
    );
  }

  return (
    <SimpleLayout title='Checkout' link='javascript:;'>

      {varify ? (
        <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-4 rounded-xl text-sm my-10 mx-auto w-full max-w-lg mx-4">
          <p className="mb-2">
            📧 A verification email has been sent to your email address. Please check your inbox and click the verification link.
          </p>
          <p>
            Didn't receive the email?{" "}

            {loading ? 'Please wait...' : success ? (
              <span className='text-green-600 font-semibold'>
                A verification link has been sent to your email.
              </span>
            ) : (
              <button
                onClick={resendHandler}
                className="text-brand-600 underline hover:text-brand-700 font-semibold"
              >
                Resend Verification Link
              </button>
            )}
          </p>
        </div>

      ) : (

        <div className="checkout-page bg-gray-50 min-h-screen">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-10 py-6">
            <div className="flex items-center gap-2.5 mb-6">
              <FiCreditCard className="text-brand-600" size={20} />
              <h4 className='font-bold text-gray-900 text-lg md:text-xl'>Choose Your Payment Method</h4>
            </div>

            <div className="md:flex-row gap-6 flex-col flex">
              {/* Payment Method Tabs */}
              <div className="w-full md:w-1/5 flex flex-col gap-2 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm h-fit">
                <button
                  className={`p-3 rounded-xl flex gap-3 items-center text-sm font-semibold transition-colors
                    ${activeTab === "razorpay" ? "bg-brand-600/10 text-brand-600" : "text-gray-500 hover:bg-gray-50"}`}
                  onClick={() => setActiveTab("razorpay")}>
                  <BiWallet className={`p-2 rounded-full text-3xl ${activeTab === 'razorpay' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-400'}`} />
                  Online Payment
                </button>
                <button
                  disabled={!codAllowed}
                  className={`p-3 rounded-xl flex gap-3 items-center text-sm font-semibold transition-colors
                    ${!codAllowed ? "text-gray-300 cursor-not-allowed" : activeTab === "cod" ? "bg-brand-600/10 text-brand-600" : "text-gray-500 hover:bg-gray-50"}`}
                  onClick={() => codAllowed && setActiveTab("cod")}>
                  <BiRupee className={`p-2 rounded-full text-3xl ${!codAllowed ? 'bg-gray-100 text-gray-300' : activeTab === 'cod' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-400'}`} />
                  Cash on Delivery
                </button>
                {!codAllowed && (
                  <p className="text-[11px] text-gray-400 px-1 -mt-1">
                    Not available for one or more items in your cart.
                  </p>
                )}
              </div>

              <div className="w-full md:w-2/5 rounded-2xl border border-gray-100 bg-white shadow-sm relative overflow-hidden h-fit">
                <div className="bg-surface-dark w-full overflow-hidden">
                  {/* Stable node — no `key` on this element, so React updates the text
                      in place every 3 seconds instead of unmounting/remounting it.
                      That remount (previously via motion.div key={currentMessage}) was
                      what was resetting page scroll position every rotation. */}
                  <div className="text-white px-4 py-2 justify-center flex items-center gap-2 text-center text-xs font-medium">
                    {messages[currentMessage].icon} <span>{messages[currentMessage].text}</span>
                  </div>
                </div>

                <div className="p-5">
                  {/* Payment Actions */}
                  {activeTab === "cod" ? (
                    <>
                      <p className='mb-1 font-bold text-gray-900'>
                        Pay Cash on Delivery
                      </p>
                      <p className='text-sm text-gray-500 mb-4'>
                        Additional cash collection charges of ₹20 is applicable on this order.
                      </p>
                      <button disabled={loading} onClick={handleSubmit}
                        className={`px-4 py-3 rounded-xl w-full font-semibold text-sm transition-colors
                          ${loading ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-700 text-white'}`}>
                        {loading ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" />
                            Please wait...
                          </span>
                        ) : 'Place Order'}
                      </button>
                    </>

                  ) : (
                    <>
                      <div className='flex items-center gap-2 mb-3'>
                        <SiRazorpay className="text-brand-600" size={18} />
                        <p className='font-bold text-gray-900'>Pay with Razorpay</p>
                      </div>
                      <div className='text-xs text-gray-500 mb-3 space-y-1'>
                        <p>✅ Multiple payment modes including UPI, cards, and net banking.</p>
                        <p>✅ Seamless Checkout – Enjoy quick and easy transactions.</p>
                        <p>✅ Trusted & Reliable – Powered by Razorpay, ensuring 100% secure payments.</p>
                      </div>

                      <p className='text-xs text-gray-500'>
                        Select your preferred payment method and complete your purchase with confidence!
                      </p>

                      <button disabled={loading} onClick={handleSubmit}
                        className={`px-4 py-3 rounded-xl w-full font-semibold text-sm mt-4 transition-colors
                          ${loading ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-700 text-white'}`}>
                        {loading ? (
                          <span className="inline-flex items-center gap-2 justify-center">
                            <span className="w-4 h-4 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" />
                            Please wait...
                          </span>
                        ) : (
                          `Pay ${new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(newSubtotal)}`
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Main Checkout Section */}
              <div className="w-full md:w-2/5 space-y-4">
                {/* Address */}
                <div className={`rounded-2xl border bg-white shadow-sm overflow-hidden ${(shippingValidate || shippingAddresses.length > 0) ? 'border-brand-600/40' : 'border-gray-100'}`}>
                  <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => setIsAddressOpen(!isAddressOpen)}>
                    <div className="flex items-center gap-2.5">
                      <FiMapPin className="text-brand-600" size={16} />
                      <h4 className="font-bold text-sm text-gray-900">Select Delivery Address</h4>
                    </div>
                    {isAddressOpen ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
                  </div>
                  {isAddressOpen && (
                    <div className="px-4 pb-4">
                      {shippingAddresses.length > 0 ? (
                        <div className="flex flex-col gap-2.5">
                          {shippingAddresses.map((address) => (
                            <div
                              key={address.id}
                              onClick={() => handleAddressSelect(address)}
                              className={`p-3.5 rounded-xl flex gap-3 cursor-pointer border transition-colors
                                ${selectedAddress?.id === address.id ? 'border-brand-600 bg-brand-600/5' : 'border-gray-200 hover:border-gray-300'}`}
                            >
                              <span
                                className={`w-5 h-5 mt-0.5 rounded-full cursor-pointer flex-shrink-0 flex items-center justify-center border
                                  ${selectedAddress?.id === address.id ? "bg-brand-600 border-brand-600 text-white" : "border-gray-300"}`}>
                                {selectedAddress?.id === address.id && <BiCheck size={14} />}
                              </span>
                              <div className='text-xs text-gray-500 flex-1'>
                                <div className='font-semibold text-gray-800 mb-1'>{address.full_name}</div>
                                <div>{address.address_line_1}</div>
                                <div>{address.city}, {address.state} - {address.zip_code}</div>
                              </div>

                              <Link
                                to="/myaccount/addresses" state={{ status: "edit", address: address, slug: "checkout" }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-gray-400 hover:text-brand-600 flex-shrink-0"
                              >
                                <FiEdit2 size={14} />
                              </Link>
                            </div>
                          ))}
                          <Link state={{ status: "add", slug: "checkout" }} className='text-brand-600 text-xs font-bold hover:underline' to="/myaccount/addresses">+ Add New Address</Link>
                        </div>
                      ) : (
                        <Link to="/myaccount/addresses" className='text-brand-600 text-xs font-bold hover:underline' state={{ status: "add", slug: "checkout" }}>+ Add your shipping address</Link>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Items */}
                <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                  <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => setIsItemsOpen(!isItemsOpen)}>
                    <div className="flex items-center gap-2.5">
                      <FiPackage className="text-brand-600" size={16} />
                      <h4 className="font-bold text-sm text-gray-900">Order Items <span className="text-gray-400 font-medium">({cartItems.length})</span></h4>
                    </div>
                    {isItemsOpen ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
                  </div>
                  {isItemsOpen && (
                    <div className="px-4 pb-4 space-y-3">
                      {cartItems.map((item, index) => {
                        const unitPrice = getItemUnitPrice(item);
                        return (
                          <div key={index} className="flex items-center gap-3">
                            {(item.custom_products[0].front_img_url && item.custom_products[0].back_img_url) === null ? (
                              <Link to={`/p/${item.slug ? item.slug : item.product.slug}`} className="rounded-xl overflow-hidden flex-shrink-0">
                                <img
                                  src={compressImage(item.primary_img, 400, 70, 'webp')}
                                  alt={item.prod_name}
                                  className="w-12 h-12 object-cover"
                                />
                              </Link>
                            ) : (
                              <img
                                src={item.custom_products[0].front_img_url ? item.custom_products[0].front_img_url : item.product.back_img_url}
                                alt={item.prod_name}
                                className="w-12 h-12 object-cover rounded-xl flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{item.prod_name}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                <p className="text-xs text-gray-500">Qty: <span className="text-green-600 font-semibold">{item.quantity}</span></p>
                                <p className="text-xs text-gray-500">
                                  ₹{unitPrice.toFixed(2)} × {item.quantity} = <span className="font-semibold text-gray-800">₹{(unitPrice * item.quantity).toFixed(2)}</span>
                                </p>
                              </div>
                            </div>
                            {cartItems.length > 1 && (
                              <button
                                onClick={() => removeCartItem(item)}
                                disabled={removingItemId === item.id}
                                aria-label="Remove item"
                                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                              >
                                {removingItemId === item.id ? (
                                  <span className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                                ) : (
                                  <FiTrash2 size={14} />
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Order Summary */}
                <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
                  <h4 className='font-bold text-sm text-gray-900 mb-3'>Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className='flex justify-between items-center text-gray-500'>
                      <span>Total MRP (Incl. of GST)</span>
                      <span className='font-medium text-gray-800'>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(totalMRP)}</span>
                    </div>
                    <div className='flex justify-between items-center text-gray-500'>
                      <span>Bag Discount</span>
                      <span className='text-green-600 font-medium'>-{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(savings)}</span>
                    </div>
                    <div className='flex justify-between items-center text-gray-500'>
                      <span>{activeTab === 'cod' ? 'COD Fee' : 'Delivery Fee'}</span>
                      <span className='text-green-600 font-medium'>{activeTab === 'cod' ? '+₹20' : 'Free'}</span>
                    </div>

                    {discount > 0 && (
                      <>
                        <div className='flex justify-between items-center text-gray-500'>
                          <span>Price</span>
                          <span className='text-green-600 font-medium'>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(newSubtotal + discount)}</span>
                        </div>
                        <div className='flex justify-between items-center text-gray-500'>
                          <span>Coupon Code Discount</span>
                          <span className='text-green-600 font-medium'>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(discount)}</span>
                        </div>
                        <div className='flex justify-between items-center text-gray-500'>
                          <span>Discount Price</span>
                          <span className='text-green-600 font-medium'>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(newSubtotal)}</span>
                        </div>
                        {roundOff > 0 && (
                          <div className='flex justify-between items-center text-gray-500'>
                            <span>Round Off</span>
                            <span className='text-green-600 font-medium'>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(roundOff)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="border-t border-dashed border-gray-200 mt-3 pt-3 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Subtotal</span>
                    <span className='text-lg font-bold text-green-700'>
                      {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
                        Math.round(`${activeTab === 'cod' ? (newSubtotal + 20) : newSubtotal}`)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      )}
    </SimpleLayout>
  );
};

export default Checkout;
